import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CheckCircle, MessageSquare, Users } from 'lucide-react';
import { LocalizedLink } from './LocalizedLink';
import { Container } from './ui/Section';

export function CTABanner() {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden py-20 lg:py-28">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-500 via-primary-400 to-info-400" />

      {/* Animated Background Shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-1/2 -right-1/4 w-[800px] h-[800px] rounded-full bg-white/5"
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, 5, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute -bottom-1/2 -left-1/4 w-[600px] h-[600px] rounded-full bg-white/5"
          animate={{
            scale: [1, 1.15, 1],
            rotate: [0, -5, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
        />
        {/* Grid Pattern */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <Container className="relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            className="text-white"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
              {t('ctaBanner.title')}
            </h2>
            <p className="text-lg text-white/90 mb-8 max-w-lg">
              {t('ctaBanner.description')}
            </p>
            <div className="flex flex-wrap gap-4">
              <motion.div
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
              >
                <LocalizedLink to="contacts">
                  <button className="inline-flex items-center justify-center font-medium rounded-lg px-8 py-3 h-12 text-base bg-white text-primary-500 font-semibold shadow-xl cursor-pointer">
                    {t('ctaBanner.contactButton')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </button>
                </LocalizedLink>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
              >
                <LocalizedLink to="about">
                  <button className="inline-flex items-center justify-center font-medium rounded-lg px-8 py-3 h-12 text-base bg-white/10 text-white border-2 border-white/30 backdrop-blur-sm cursor-pointer">
                    {t('ctaBanner.aboutButton')}
                  </button>
                </LocalizedLink>
              </motion.div>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-6 mt-8 text-sm text-white/80">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span>{t('ctaBanner.freeConsultation')}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span>{t('ctaBanner.quote24h')}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span>{t('ctaBanner.noCommitment')}</span>
              </div>
            </div>
          </motion.div>

          {/* Right Side - Decorative Cards */}
          <motion.div
            className="relative hidden lg:block"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative">
              {/* Main Card */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-white font-semibold">{t('ctaBanner.letsTalk')}</div>
                    <div className="text-white/70 text-sm">{t('ctaBanner.response24h')}</div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="h-2 bg-white/20 rounded-full w-full" />
                  <div className="h-2 bg-white/20 rounded-full w-3/4" />
                  <div className="h-2 bg-white/20 rounded-full w-1/2" />
                </div>
              </div>

              {/* Floating elements - Static */}
              <div className="absolute -top-4 -right-4 bg-white rounded-xl p-3 shadow-xl">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-accent-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-accent-500" />
                  </div>
                  <span className="text-sm font-medium text-gray-800">{t('ctaBanner.requestSent')}</span>
                </div>
              </div>

              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-3 shadow-xl">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-primary-500" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-800">{t('ctaBanner.clientsThisMonth')}</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
}
